import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import getPalette from './palette';
import typography from './typography';
import { ColorMode, DesignTokens, getTokens } from './tokens';

// Expose the raw design tokens on the theme so components can reach the
// design-specific values MUI's palette has no slot for (surface variants,
// tertiary text, status pill colors, the mono font).
declare module '@mui/material/styles' {
  interface Theme {
    tokens: DesignTokens;
  }
  interface ThemeOptions {
    tokens?: DesignTokens;
  }
}

// 34x19 track with a 15px thumb (design §8). Shared by the `root` and
// `sizeSmall` slots — MUI resolves sizeSmall last and would override root.
const switchMetrics = {
  width: 34,
  height: 19,
  padding: 0,
  // inline-flex, not flex: a block-level box ignores the surrounding
  // text-align, which would strand the switch on the left of a centred cell.
  display: 'inline-flex',
} as const;

/**
 * Build the full MUI theme for a color mode. Component overrides are written
 * against the tokens so they stay correct in both dark and light.
 */
export const createAppTheme = (mode: ColorMode): Theme => {
  const tokens = getTokens(mode);

  const themeOptions: ThemeOptions = {
    tokens,
    palette: getPalette(mode),
    typography,
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: tokens.bg,
            color: tokens.textPrimary,
            WebkitFontSmoothing: 'antialiased',
          },
          '::-webkit-scrollbar': { width: 6, height: 6 },
          '::-webkit-scrollbar-track': { background: 'transparent' },
          '::-webkit-scrollbar-thumb': {
            background: tokens.border2,
            borderRadius: 99,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            fontWeight: 600,
            transition:
              'transform 0.15s ease, filter 0.2s ease, background-color 0.2s ease',
            // The design lifts every button on hover, not just contained ones.
            '&:hover': { filter: 'brightness(1.13)' },
            '&:active': { transform: 'scale(0.97)' },
            '&:focus-visible': {
              outline: `2px solid ${tokens.accent}`,
              outlineOffset: 2,
            },
            // Disabled buttons keep their tint and dim, rather than taking MUI's
            // grey wash — each variant below restates its fill to hold that.
            '&.Mui-disabled': { opacity: 0.45 },
          },

          sizeSmall: {
            padding: '7px 14px',
            borderRadius: 7,
            fontSize: '0.75rem',
          },
          textSizeSmall: { padding: '6px 10px' },
          textSizeMedium: { padding: '6px 10px' },

          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          containedPrimary: {
            backgroundColor: tokens.accent,
            color: tokens.onAccent,
            '&:hover': { backgroundColor: tokens.accent },
            '&.Mui-disabled': {
              backgroundColor: tokens.accent,
              color: tokens.onAccent,
            },
          },
          containedError: {
            backgroundColor: '#DC2626',
            color: '#FFFFFF',
            '&:hover': { backgroundColor: '#DC2626' },
            '&.Mui-disabled': { backgroundColor: '#DC2626', color: '#FFFFFF' },
          },

          outlinedPrimary: {
            backgroundColor: tokens.accentDim,
            color: tokens.accent,
            borderColor: tokens.accent,
            '&:hover': {
              backgroundColor: tokens.accentDim,
              borderColor: tokens.accent,
            },
            '&.Mui-disabled': {
              backgroundColor: tokens.accentDim,
              color: tokens.accent,
              borderColor: tokens.accent,
            },
          },
          // The "soft" secondary button. MUI has no neutral color slot, so this
          // rides on color="inherit", whose own default is grey-based.
          outlinedInherit: {
            backgroundColor: tokens.surface2,
            color: tokens.textSecondary,
            borderColor: tokens.border,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: tokens.surface2,
              borderColor: tokens.border,
            },
            '&.Mui-disabled': {
              backgroundColor: tokens.surface2,
              color: tokens.textSecondary,
              borderColor: tokens.border,
            },
          },
          outlinedError: {
            backgroundColor: tokens.status.failed.bg,
            color: tokens.status.failed.fg,
            borderColor: 'rgba(239,68,68,0.3)',
            '&:hover': {
              backgroundColor: tokens.status.failed.bg,
              borderColor: 'rgba(239,68,68,0.3)',
            },
            '&.Mui-disabled': {
              backgroundColor: tokens.status.failed.bg,
              color: tokens.status.failed.fg,
              borderColor: 'rgba(239,68,68,0.3)',
            },
          },

          textPrimary: {
            color: tokens.accent,
            '&:hover': { backgroundColor: tokens.surface2 },
            '&.Mui-disabled': { color: tokens.accent },
          },
          textInherit: {
            color: tokens.textSecondary,
            '&:hover': { backgroundColor: tokens.surface2 },
            '&.Mui-disabled': { color: tokens.textSecondary },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          // The design has a single switch size, so `size="small"` gets the same
          // metrics as the default — MUI would otherwise reimpose 40x24 with a
          // 16px thumb, which overflows a 19px track.
          root: switchMetrics,
          sizeSmall: switchMetrics,
          switchBase: {
            padding: 2,
            // The thumb draws itself in currentColor.
            color: tokens.textTertiary,
            '&.Mui-checked': {
              transform: 'translateX(15px)',
              color: '#FFFFFF',
              '& + .MuiSwitch-track': {
                backgroundColor: tokens.accent,
                borderColor: 'transparent',
                opacity: 1,
              },
            },
            '&.Mui-disabled': { opacity: 0.45 },
            '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.45 },
          },
          thumb: {
            width: 15,
            height: 15,
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          },
          track: {
            borderRadius: 10,
            boxSizing: 'border-box',
            backgroundColor: tokens.surface2,
            border: `1px solid ${tokens.border2}`,
            opacity: 1,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: tokens.surface,
            border: `1px solid ${tokens.border}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: tokens.shadow,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.headerBg,
            color: tokens.textPrimary,
            boxShadow: 'none',
            borderBottom: `1px solid ${tokens.border}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.surface,
            borderRight: `1px solid ${tokens.border}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '12px 16px',
            borderColor: tokens.border,
          },
          head: {
            fontSize: '10.5px',
            fontWeight: 600,
            color: tokens.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: tokens.rowHover },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

// Default export kept for backwards compatibility (dark is the design default).
const theme = createAppTheme('dark');

export default theme;
